import * as CycleHttp from '../../../../services/api/https/deliveries/delivery-cycle.http'
import { useMemo, useState, useCallback } from 'react'
import {
  getPeriodoMesAtual,
  parseToMysqlFormatWithTime,
} from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'
import { useCicloEntregaContext } from '../../../../contexts/EntregaContext'
import { menuItemsAllowed } from '../../../../services/permission/menus'

const MENU_ITEMS = ['1. Editar', '2. Importação', '3. Trechos', '4. Ações']

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  GET_DELIVERY_CICLES: 'Adquirindo ciclos de entrega',
  DELETE: 'Deletando ciclo de entrega',
  SUCCESS_DELETE: 'Ciclo de entrega deletado com sucesso',
  GET_DELIVERY_CICLES_FILTRO: 'Buscando por empresa + NF',
}

const MODAL_INIT_STATE = {
  id: null,
  name: '',
  created_at: new Date(),
  deadline: '',
  company_id: '',
  company_de: null,
  operationcenter_id: '',
  file_type_id: '',
  type: 'crossdocking',
  companies: [],
  selectedCompany: null,
  notaFiscal: '',
}

const INITIAL_STATE = {
  selectedCentral: '',
  selectedCiclo: {},
  ciclos: [],
  modal: { show: false, data: { ...MODAL_INIT_STATE } },
  modalConfirmDelete: { show: false, data: null },
  periodo: getPeriodoMesAtual(),
  pathToRedirect: null,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  globalFilter: '',
  notaFiscal: '',
}

export default function useCicloEntrega() {
  const context = useCicloEntregaContext()

  const [state, setState] = useState({
    ...INITIAL_STATE,
    periodo: context.cicloEntregaContext.selectedPeriodo,
    selectedCentral: context.cicloEntregaContext.selectedCentral,
    globalFilter: context.cicloEntregaContext.globalFilter,
  })

  function buildCompaniesList(ciclos) {
    const uniques = []
    ciclos.forEach(c => {
      if (!uniques.find(u => u.id === c.company_id)) {
        uniques.push({ id: c.company_id, name: c.company })
      }
    })
    return uniques.sort((a, b) => a.name.localeCompare(b.name))
  }

  const onFiltroChange = useCallback((name, value) => {
    setState(p => ({ ...p, [name]: value }))
  }, [])

  const onBuscarFiltrado = useCallback(() => {
    const { selectedCentral, periodo, selectedCompany, notaFiscal = '' } = state

    if (!selectedCompany || notaFiscal.trim() === '') {
      setState(p => ({ ...p, errorMessage: 'Selecione a empresa e digite a NF' }))
      return
    }

    setState(p => ({ ...p, loadingMessage: LOAD_MESSAGE.GET_DELIVERY_CICLES_FILTRO }))

    CycleHttp.getDeliveriesCyclesByNF(
      selectedCentral,
      parseToMysqlFormatWithTime(periodo.dataInicial),
      parseToMysqlFormatWithTime(periodo.dataFinal),
      selectedCompany.id,
      notaFiscal.trim(),
    )
      .then(ciclos => setState(p => ({ ...p, ciclos, loadingMessage: LOAD_MESSAGE.NONE })))
      .catch(err =>
        setState(p => ({
          ...p,
          errorMessage: errorHandler(err),
          loadingMessage: LOAD_MESSAGE.NONE,
        })),
      )
  }, [
    state.selectedCentral,
    state.periodo.dataInicial,
    state.periodo.dataFinal,
    state.selectedCompany,
    state.notaFiscal,
  ])

  const coreDispatch = useMemo(() => {
    // Helpers internos
    const resetModal = () => ({ show: false, data: { ...MODAL_INIT_STATE } })
    const toggleModal = show => setState(p => ({ ...p, modal: { show, data: MODAL_INIT_STATE } }))

    return {
      deleteDeliveryCycle: async function (ciclo) {
        try {
          // Excluir o ciclo normalmente
          await CycleHttp.deleteDeliveryCycle(ciclo.id)
          setState(p => ({
            ...p,
            ciclos: p.ciclos.filter(c => c.id !== ciclo.id),
            loadingMessage: LOAD_MESSAGE.SUCCESS_DELETE,
          }))
        } catch (err) {
          setState(p => ({
            ...p,
            errorMessage: errorHandler(err),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onBtnNovo() {
        toggleModal(true)
      },
      onModalClose() {
        setState(p => ({ ...p, modal: resetModal() }))
      },
      onModalSave(ciclo) {
        setState(p => {
          const idx = p.ciclos.findIndex(l => l.id === ciclo.id)
          let novosCiclos

          if (idx !== -1) {
            novosCiclos = [...p.ciclos]
            novosCiclos[idx] = ciclo
          } else {
            novosCiclos = [ciclo, ...p.ciclos]
          }

          return {
            ...p,
            ciclos: novosCiclos,
            companies: buildCompaniesList(novosCiclos),
            modal: resetModal(),
          }
        })
      },

      onMenuItemClicked(item, ciclo) {
        let path = ''
        switch (item) {
          case '1. Editar':
            this.onMenuItemEditarClicked(ciclo)
            return
          case 'Apagar':
            setState(p => ({
              ...p,
              modalConfirmDelete: { show: true, data: { ...ciclo } },
            }))
            return
          case '2. Importação':
            if (ciclo.tipo === 'excel') path = '/main/entregas/importacao-ciclo-entrega'
            else if (ciclo.tipo === 'xml') path = '/main/entregas/importacao-ciclo-entrega-xml'
            else if (ciclo.tipo === 'pdf') path = '/main/entregas/importacao-ciclo-entrega-pdf'
            else if (ciclo.tipo === 'edi') path = '/main/entregas/importacao-ciclo-entrega-edi'
            else if (ciclo.tipo === 'rtf') path = '/main/entregas/importacao-ciclo-entrega-rtf'
            break
          case '3. Trechos':
            path = '/main/entregas/trechos'
            break
          case '4. Ações':
            path = '/main/entregas/ciclo-entrega-acoes'
            break
          default:
            break
        }
        setState(p => ({
          ...p,
          selectedCiclo: ciclo,
          pathToRedirect: path || null,
        }))
      },

      onMenuItemEditarClicked(ciclo) {
        setState(p => ({ ...p, modal: { show: true, data: ciclo } }))
      },
      onMenuItemApagarClicked(ciclo) {
        setState(p => ({
          ...p,
          loadingMessage: LOAD_MESSAGE.DELETE,
          selectedCiclo: ciclo,
          modalConfirmDelete: { show: false, data: null },
        }))
      },

      onCentralOperacoesChange(centralId, pInicial, pFinal) {
        setState(p => ({
          ...p,
          selectedCentral: centralId,
          periodo: { dataInicial: pInicial, dataFinal: pFinal },
          loadingMessage: LOAD_MESSAGE.GET_DELIVERY_CICLES,
        }))
        context.setCicloEntregaContext(c => ({ ...c, selectedCentral: centralId }))
      },
      onPesquisar(pInicial, pFinal) {
        context.setCicloEntregaContext(c => ({
          ...c,
          selectedPeriodo: { dataInicial: pInicial, dataFinal: pFinal },
        }))
        setState(p => ({
          ...p,
          periodo: { dataInicial: pInicial, dataFinal: pFinal },
          loadingMessage: LOAD_MESSAGE.GET_DELIVERY_CICLES,
        }))
      },

      resetState() {
        setState(p => ({ ...p, loadingMessage: LOAD_MESSAGE.NONE, errorMessage: null }))
      },
      getMenuItems() {
        return MENU_ITEMS
      },
      verifyMenuItemAllowed(menuItem) {
        return menuItemsAllowed([menuItem]).length > 0
      },
      getDeliveriesCycles(centralId, dataInicial, dataFinal) {
        CycleHttp.getDeliveriesCycles(
          centralId,
          parseToMysqlFormatWithTime(dataInicial),
          parseToMysqlFormatWithTime(dataFinal),
        )
          .then(ciclos =>
            setState(p => ({
              ...p,
              ciclos,
              companies: buildCompaniesList(ciclos),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(p => ({
              ...p,
              errorMessage: errorHandler(err),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
      },
      onModalDeleteClose() {
        setState(p => ({ ...p, modalConfirmDelete: { show: false, data: null } }))
      },
      onModalDeleteSave(ciclo) {
        this.onMenuItemApagarClicked(ciclo)
      },
      onGerarMultiCiclos() {
        setState(p => ({
          ...p,
          pathToRedirect: '/main/entregas/importacao-multi-ciclo-entrega-xml',
        }))
      },
      setGlobalFilter(value) {
        setState(p => ({ ...p, globalFilter: value }))
        context.setCicloEntregaContext(c => ({ ...c, globalFilter: value }))
      },
    }
  }, [])
  const localDispatch = useMemo(
    () => ({ ...coreDispatch, onFiltroChange, onBuscarFiltrado }),
    [coreDispatch, onFiltroChange, onBuscarFiltrado],
  )

  return { state, localDispatch }
}
